import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { X, User, Lock, Loader2, ArrowLeft, Shield, UserCircle, UserPlus, Eye, EyeOff } from 'lucide-react';
import LogoPlaceholder from '../ui/LogoPlaceholder';
import { authAPI, adminAPI } from '../../services/api';

export function LoginForm({ onClose, onLogin, onSwitchToSignUp, onSwitchToAdminApplication }) {
  const [loginType, setLoginType] = useState('user'); // 'user' or 'admin'
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(''); // Clear error when user types
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      // Call appropriate API based on login type
      const response = loginType === 'admin' 
        ? await adminAPI.login({
            username: formData.username,
            password: formData.password
          })
        : await authAPI.login({
            username: formData.username,
            password: formData.password
          });
      // Both APIs return { success, message, token, user }
      if (response.success && response.user) {
        // Verify role matches login type
        if (loginType === 'admin' && response.user.role !== 'admin') {
          setError('This account does not have admin privileges');
          return;
        }
        if (loginType === 'user' && response.user.role === 'admin') {
          setError('Admin accounts must use Admin login');
          return;
        }

        // Transform backend user data to match profile component expectations
        const transformedUser = {
          ...response.user,
          id: response.user.id || response.user.User_ID,
          firstName: response.user.firstName || response.user.First_Name || response.user.name?.split(' ')[0] || '',
          lastName: response.user.lastName || response.user.Last_Name || response.user.name?.split(' ').slice(1).join(' ') || '',
          phone: response.user.phone || response.user.Phone_Number || '',
          organization: response.user.organization || response.user.Organization || '',
          municipality: response.user.municipality || response.user.Municipality || '',
          avatar: response.user.avatar || response.user.Avatar || null
        };
        
        onLogin(transformedUser);
        onClose();
      } else {
        setError(response.message || 'Login failed');
      }
    } catch (error) {
      setError(error.message || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const modalRef = useRef(null);

  const handleEscape = useCallback((e) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [handleEscape]);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 backdrop-blur-sm animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-labelledby="login-dialog-title"
      ref={modalRef}
    >
      <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl transform transition-all duration-300 animate-scale-in">
        <Card className="border-0 shadow-none">
          <CardHeader className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <LogoPlaceholder size="medium" showText={false} className="" />
                <CardTitle className="text-amber-900" id="login-dialog-title">Welcome Back</CardTitle>
              </div>
              <Button variant="ghost" onClick={onClose} className="text-gray-500 hover:text-gray-700 rounded-full h-8 w-8 p-0 transition-all duration-200 hover:bg-gray-100" aria-label="Close">
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            {/* Login Type Switcher */}
            <div className="flex gap-2 p-1 bg-gray-100 rounded-lg">
              <button
                type="button"
                onClick={() => {
                  setLoginType('user');
                  setError('');
                }}
                className={`flex-1 py-2.5 px-4 rounded-md transition-all duration-300 flex items-center justify-center gap-2 font-medium
                  ${loginType === 'user' 
                    ? 'bg-white text-amber-700 shadow-md' 
                    : 'text-gray-600 hover:text-gray-800'}`}
              >
                <UserCircle className="w-4 h-4" />
                <span>User</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setLoginType('admin');
                  setError('');
                }}
                className={`flex-1 py-2.5 px-4 rounded-md transition-all duration-300 flex items-center justify-center gap-2 font-medium
                  ${loginType === 'admin' 
                    ? 'bg-white text-green-700 shadow-md' 
                    : 'text-gray-600 hover:text-gray-800'}`}
              >
                <Shield className="w-4 h-4" />
                <span>Admin</span>
              </button>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Helper Text */}
              <p className="text-sm text-gray-600 text-center">
                {loginType === 'admin' 
                  ? 'Sign in with your admin credentials' 
                  : 'Sign in to continue to your account'}
              </p>

              {error && (
                <Alert className="border-red-200 bg-red-50">
                  <AlertDescription className="text-red-700">{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="username">Username</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    id="username"
                    value={formData.username}
                    onChange={(e) => handleInputChange('username', e.target.value)}
                    placeholder="Enter your username"
                    className="pl-10 rounded-xl transition-all duration-200 focus:ring-2 focus:ring-amber-500"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    placeholder="Enter your password"
                    className="pl-10 pr-10 rounded-xl transition-all duration-200 focus:ring-2 focus:ring-amber-500"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-3">
                <Button 
                  type="submit" 
                  className={`w-full flex items-center justify-center gap-2 ${
                    loginType === 'admin' 
                      ? 'bg-green-600 hover:bg-green-700' 
                      : 'bg-amber-600 hover:bg-amber-700'
                  }`}
                  disabled={isLoading}
                >
                  {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                  {isLoading ? 'Signing In...' : 'Sign In'}
                </Button>
                
                {loginType === 'user' && (
                  <Button 
                    type="button"
                    variant="outline" 
                    onClick={onSwitchToSignUp}
                    className="w-full"
                  >
                    Don't have an account? Sign Up
                  </Button>
                )}

                {/* Apply as Admin Button - shown on both login types */}
                <Button 
                  type="button"
                  variant="outline"
                  onClick={() => {
                    if (onSwitchToAdminApplication) {
                      onSwitchToAdminApplication();
                    }
                  }}
                  className="w-full flex items-center justify-center gap-2 border-green-600 text-green-700 hover:bg-green-50"
                >
                  <UserPlus className="h-4 w-4" />
                  Apply as Admin
                </Button>

                {/* Switch Login Type Link */}
                <button
                  type="button"
                  onClick={() => {
                    setLoginType(loginType === 'admin' ? 'user' : 'admin');
                    setError('');
                  }}
                  className="w-full text-sm text-gray-500 hover:text-amber-600 transition-colors text-center"
                >
                  {loginType === 'admin' 
                    ? 'Switch to User Login' 
                    : 'Switch to Admin Login'}
                </button>

                <Button 
                  type="button"
                  variant="ghost" 
                  onClick={onClose}
                  className="w-full text-gray-600 hover:text-gray-800 flex items-center justify-center gap-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to Welcome Page
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}